<template>
    <div class="modal">
        <div class="modal-container" v-if="credits">
            <div class="modal-header">
                <close-icon v-on:click="closeView"></close-icon>
                <h1>Order Credits</h1>
            </div>

            <div class="modal-body no-pad">
                <div class="flex h-full">
                    <div class="w-full md:w-full">
                        <div class="account-body">
                            <div class="results-list">
                                <div class="results-row">
                                    <div class="results-head" data-width="20%">Date</div>
                                    <div class="results-head" data-width="20%">Value</div>
                                    <div class="results-head" data-width="20%">Used</div>
                                    <div class="results-head" data-width="40%">Description</div>
                                </div>
                                <template v-if="credits">
                                    <div class="results-row" v-for="(credit, index) in credits" :key="index"
                                         @click="handleViewCredit(credit)">
                                        <div class="results-cell" data-width="20%">
                                            {{ moment(credit.created_at).format('DD/MM/YYYY') }}
                                        </div>
                                        <div class="results-cell" data-width="20%">&pound;{{ credit.value }}</div>
                                        <div class="results-cell" data-width="20%">&pound;{{
                                                valueUsedForCredit(credit)
                                            }}
                                        </div>
                                        <div class="results-cell" data-width="40%">{{ credit.description }}</div>
                                    </div>
                                </template>
                                <div class="results-row" v-if="!credits">
                                    <div class="results-span text-center" data-width="100%">
                                        <p>No results found</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="modal-footer">
                <div class="flex">
                    <div class="left text-left">
                        <button class="btn-primary muted" type="button" @click="handleAddCredit">Add Credit</button>
                    </div>

                    <div class="middle text-middle">

                    </div>

                    <div class="right text-right">
                        <button class="btn-tertiary muted" type="button" @click="closeView">Close</button>
                    </div>
                </div>
            </div>

            <router-view v-slot="{ Component }">
                <transition name="drop-in" mode="in-out">
                    <component :is="Component"></component>
                </transition>
            </router-view>

            <mini-modal name="manageCredit" :overlay="true" title="Account Credit">
                <div v-if="selectedCredit">
                    <div class="alert danger text-sm" v-if="!creditCanBeEdited(selectedCredit)">
                        <p>Credits which have been used or partially used cannot be edited</p>
                    </div>

                    <div class="form-field styled">
                        <label>Description</label>
                        <input type="text" v-model="selectedCredit.description"
                               :disabled="!creditCanBeEdited(selectedCredit)">
                    </div>

                    <div class="form-field styled">
                        <label>Value (&pound;)</label>
                        <input type="text" v-model="selectedCredit.value"
                               :disabled="!creditCanBeEdited(selectedCredit)">
                        <p class="text-xs text-gray-600 mt-1" v-if="creditCanBeEdited(selectedCredit)">To remove this
                            credit, set the value to zero and save.</p>
                    </div>

                    <div class="form-field styled">
                        <label>Amount Used (&pound;)</label>
                        <input type="text" :value="valueUsedForCredit(selectedCredit)" :disabled="true">
                    </div>

                    <button class="btn-primary" v-if="creditCanBeEdited(selectedCredit)" @click="saveSelectedCredit">
                        Save
                    </button>
                    <button class="btn-tertiary muted" @click="$bus.$emit('minimodal.hide', 'manageCredit')">Close
                    </button>
                </div>
            </mini-modal>
        </div>
    </div>
</template>

<script>
import MiniModal from '../../MiniModal'

export default {
    name: 'AccountCreditsView',
    components: {
        MiniModal,
    },
    props: {
        //
    },
    data() {
        return {
            credits: null,
            creditUses: null,
            selectedCredit: null,
        }
    },
    mounted() {
        this.loadData()

        this.setMetaTitle('Account Credits | JournoLink Control')
    },
    methods: {
        loadData() {
            axios.get(route('accounts.credits', this.$route.params.accountId))
                .then(response => {
                    this.credits = response.data.account.order_credits
                    this.creditUses = response.data.account.orders.filter((order) => {
                        return order.credits.length > 0
                    }).map((order) => {
                        return order.credits
                    }).flat()
                })
                .catch(e => {
                    console.error({e})
                })
        },
        /**
         * Handle a click event to view a credit
         *
         * @param  {Object} credit
         * @return {void}
         */
        handleViewCredit(credit) {
            this.selectedCredit = credit
            this.$bus.$emit('minimodal.show', 'manageCredit')
        },
        handleAddCredit() {
            this.selectedCredit = {
                description: null,
                value: 0.0,
            }
            this.$bus.$emit('minimodal.show', 'manageCredit')
        },
        /**
         * Retrieve all the uses for a given credit
         *
         * @param  {Object} credit
         * @return {Array}
         */
        creditUsesForCredit(credit) {
            return this.creditUses.filter((use) => {
                return use.order_credit_id === credit.id
            }) || []
        },
        /**
         * Get the total used value for a credit
         *
         * @param  {Object} credit
         * @return {Number}
         */
        valueUsedForCredit(credit) {
            return this.creditUsesForCredit(credit).reduce((carry, item) => {
                return parseFloat(carry) + parseFloat(item.value)
            }, 0).toFixed(2)
        },
        /**
         * Determine if the given credit can be edited
         *
         * @param  {Object} credit
         * @return {Boolean}
         */
        creditCanBeEdited(credit) {
            return this.valueUsedForCredit(credit) == 0
        },
        /**
         * Save the selected credit to the Account
         *
         * @return {void}
         */
        saveSelectedCredit() {
            if (this.selectedCredit.id !== undefined) {
                return this.saveExistingCredit()
            }

            return this.saveNewCredit()
        },
        /**
         * Update an existing credit
         *
         * @return {void}
         */
        saveExistingCredit() {
            axios.put(
                route('accounts.credits.update', {
                    account: this.$route.params.accountId,
                    credit: this.selectedCredit.id
                }),
                this.selectedCredit
            )
                .then(response => {
                    this.$bus.$emit('alert.success', 'Account credit has been saved')
                    this.$bus.$emit('minimodal.hide', 'manageCredit')
                    this.selectedCredit = null
                    this.loadData()
                })
                .catch((e) => {
                    console.error({e})
                })
        },
        /**
         * Save a new credit
         *
         * @return {void}
         */
        saveNewCredit() {
            axios.post(route('accounts.credits.create', {account: this.$route.params.accountId}), this.selectedCredit)
                .then(response => {
                    this.$bus.$emit('alert.success', 'Account credit has been saved')
                    this.$bus.$emit('minimodal.hide', 'manageCredit')
                    this.selectedCredit = null
                    this.loadData()
                })
                .catch((e) => {
                    console.error({e})
                })
        },
        /**
         * Emit the close event to the parent
         *
         * @return {void}
         */
        closeView() {
            this.$root.goBack()
        },
        /**
         * Handle an [Escape] keypress
         *
         * @return {void}
         */
        onEscapeKeypress() {
            this.closeView()
        },
    },
    computed: {
        creditTotal() {
            return this.credits.reduce((carry, credit) => {
                return parseFloat(carry) + parseFloat(credit.value)
            }, 0).toFixed(2)
        },

        creditUsesTotal() {
            return this.creditUses.reduce((carry, use) => {
                return parseFloat(carry) + parseFloat(use.value)
            }, 0).toFixed(2)
        },

        creditRemain() {
            return parseFloat(this.creditTotal - this.creditUsesTotal).toFixed(2)
        },
    },
}
</script>

<style lang="less" scoped>
.vdp-datepicker {
    display: inline-block;
    border: 1px solid #dddddd;
    padding: 5px;
}

.modal-body {
    h2, h3 {
        text-transform: uppercase;
        font-size: 1.2em;
        letter-spacing: .08em;
        color: #34495e;
        margin-bottom: 10px;
    }
}

p.note {
    margin: 10px 0;
    font-weight: bold;
}
</style>
